/**
 * PaletteTrack — coordinated layout for a column of edge palettes.
 *
 * Owns the shared layout of sibling palettes that share a viewport edge so
 * they never overlap. Each palette registers itself via `usePaletteTrackSlot`
 * and reports its rendered height via ResizeObserver; the track computes each
 * slot's final `top` and `maxHeight` and exposes them back through the hook.
 *
 * Top-anchored slots stack down from `EDGE_PALETTE_MIN_TOP`; bottom-anchored
 * slots stack up from the viewport bottom minus `bottomReservedPx` (which
 * accounts for the bottom toolbar). When top and bottom stacks would collide,
 * each palette is clamped against its sibling's live edge — not against a
 * hand-summed constant — with a `SIBLING_GAP` of 8px between them.
 *
 * A palette's persisted drag position is stored as an offset from its anchor,
 * not as an absolute `top`. That way, when a neighbor shrinks (e.g. content
 * reflow, mode change), the palette springs back toward the user's desired
 * position instead of getting stuck at a stale clamp.
 */

import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  EDGE_PALETTE_MIN_SCROLLABLE_HEIGHT,
  EDGE_PALETTE_MIN_TOP,
} from "./EdgePalette.tsx";

/** Gap between adjacent palettes in the same track. */
export const PALETTE_SIBLING_GAP = 8;

export type PaletteAnchor = "top" | "bottom";

export interface PaletteSlotRegistration {
  id: string;
  order: number;
  anchor: PaletteAnchor;
  storageKey: string;
  /** Initial offset from the anchor edge when no persisted value exists. */
  defaultOffsetPx?: number;
  minScrollablePx?: number;
}

export interface PaletteSlotLayout {
  top: number;
  maxHeight: number;
}

interface SlotState {
  id: string;
  order: number;
  anchor: PaletteAnchor;
  storageKey: string;
  defaultOffsetPx: number;
  minScrollablePx: number;
  measuredHeight: number;
  desiredOffset: number;
}

interface TrackContextValue {
  bottomReservedPx: number;
  topReservedPx: number;
  register(reg: PaletteSlotRegistration): void;
  unregister(id: string): void;
  reportHeight(id: string, heightPx: number): void;
  setDesiredOffset(id: string, offsetPx: number, persist: boolean): void;
  getLayout(id: string): PaletteSlotLayout;
  subscribe(listener: () => void): () => void;
}

const PaletteTrackContext = createContext<TrackContextValue | null>(null);

function loadDesiredOffset(storageKey: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw != null) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  } catch {
    /* ignore */
  }
  return fallback;
}

function saveDesiredOffset(storageKey: string, offsetPx: number): void {
  try {
    localStorage.setItem(storageKey, String(offsetPx));
  } catch {
    /* ignore */
  }
}

function useViewportHeight(): number {
  const [vh, setVh] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerHeight,
  );
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return vh;
}

interface LayoutInputs {
  viewportHeight: number;
  topReservedPx: number;
  bottomReservedPx: number;
  slots: readonly SlotState[];
}

interface LayoutResult {
  byId: Map<string, PaletteSlotLayout>;
}

function clampDesiredOffset(slot: SlotState, offsetPx: number): number {
  if (slot.anchor === "top") return Math.max(0, offsetPx);
  const naturalHeight = Math.max(slot.minScrollablePx, slot.measuredHeight);
  const minOffset = slot.minScrollablePx - naturalHeight;
  return Math.max(minOffset, offsetPx);
}

/**
 * Compute per-slot layout from current slot state.
 *
 * Algorithm:
 *   1. Partition slots by anchor, sort by `order` ascending (order=0 is
 *      closest to the anchor edge; later orders stack further inward).
 *   2. Top-anchored slots build down from `topReservedPx`. Each slot's
 *      initial top is `cursor + desiredOffset` (clamped to at least
 *      `cursor`), and after placing, cursor advances by measuredHeight + GAP.
 *   3. Bottom-anchored slots build up from `viewportHeight - bottomReservedPx`.
 *      Each slot's initial bottom is `cursor - desiredOffset`, and cursor
 *      retreats by measuredHeight + GAP.
 *   4. Collision: if the last top-anchored slot's bottom exceeds the first
 *      bottom-anchored slot's top, both get clamped so they split the
 *      available column, each respecting `minScrollablePx`.
 *   5. Each slot's maxHeight is the distance from its top to the next barrier
 *      (sibling edge or reserved inset); content scrolls inside that box.
 */
function computeLayout(inputs: LayoutInputs): LayoutResult {
  const { viewportHeight, topReservedPx, bottomReservedPx, slots } = inputs;
  const byId = new Map<string, PaletteSlotLayout>();

  const topSlots = slots
    .filter((s) => s.anchor === "top")
    .slice()
    .sort((a, b) => a.order - b.order);
  const bottomSlots = slots
    .filter((s) => s.anchor === "bottom")
    .slice()
    .sort((a, b) => a.order - b.order);

  // Build top-anchored positions provisionally.
  const topPositions: { id: string; top: number; h: number; min: number }[] =
    [];
  let topCursor = Math.max(0, topReservedPx);
  for (const slot of topSlots) {
    const h = Math.max(slot.minScrollablePx, slot.measuredHeight);
    const desiredTop = topCursor + Math.max(0, slot.desiredOffset);
    topPositions.push({
      id: slot.id,
      top: desiredTop,
      h,
      min: slot.minScrollablePx,
    });
    topCursor = desiredTop + h + PALETTE_SIBLING_GAP;
  }

  // Build bottom-anchored positions provisionally (cursor is the bottom edge
  // of the next palette, measured from viewport top).
  const bottomPositions: { id: string; top: number; h: number; min: number }[] =
    [];
  let bottomCursor = Math.max(0, viewportHeight - bottomReservedPx);
  for (const slot of bottomSlots) {
    const h = Math.max(slot.minScrollablePx, slot.measuredHeight);
    const desiredBottom = bottomCursor - slot.desiredOffset;
    const top = desiredBottom - h;
    bottomPositions.push({ id: slot.id, top, h, min: slot.minScrollablePx });
    bottomCursor = top - PALETTE_SIBLING_GAP;
  }

  // Resolve collision between the inner-most top slot and the inner-most
  // bottom slot. These are the last entries of each list.
  const innerTop = topPositions[topPositions.length - 1];
  const innerBottom = bottomPositions[bottomPositions.length - 1];

  if (innerTop && innerBottom) {
    const outerTopBarrier =
      topPositions.length > 1
        ? topPositions[topPositions.length - 2].top +
          topPositions[topPositions.length - 2].h +
          PALETTE_SIBLING_GAP
        : topReservedPx;
    const outerBottomBarrier =
      bottomPositions.length > 1
        ? bottomPositions[bottomPositions.length - 2].top - PALETTE_SIBLING_GAP
        : viewportHeight - bottomReservedPx;

    const available =
      outerBottomBarrier - outerTopBarrier - PALETTE_SIBLING_GAP;
    const needed = innerTop.h + innerBottom.h;

    if (available < needed) {
      // Viewport too short: allocate proportionally, each at least min.
      const totalMin = innerTop.min + innerBottom.min;
      if (available <= totalMin) {
        // Assign the floor and let both scroll; may still be cramped.
        innerTop.top = outerTopBarrier;
        innerTop.h = innerTop.min;
        innerBottom.top = outerBottomBarrier - innerBottom.min;
        innerBottom.h = innerBottom.min;
      } else {
        const slack = available - totalMin;
        const totalMeasured = innerTop.h + innerBottom.h;
        const topShare =
          innerTop.min +
          (totalMeasured > 0
            ? slack * (innerTop.h / totalMeasured)
            : slack / 2);
        innerTop.top = outerTopBarrier;
        innerTop.h = topShare;
        innerBottom.top = outerTopBarrier + topShare + PALETTE_SIBLING_GAP;
        innerBottom.h = outerBottomBarrier - innerBottom.top;
      }
    } else {
      // Normal case: clamp each against its sibling's live edge.
      const topMin = outerTopBarrier;
      const topMax = innerBottom.top - PALETTE_SIBLING_GAP - innerTop.min;
      if (innerTop.top > topMax) innerTop.top = topMax;
      if (innerTop.top < topMin) innerTop.top = topMin;

      const bottomMin = innerTop.top + innerTop.min + PALETTE_SIBLING_GAP;
      const bottomMax = outerBottomBarrier - innerBottom.min;
      if (innerBottom.top < bottomMin) innerBottom.top = bottomMin;
      if (innerBottom.top > bottomMax) innerBottom.top = bottomMax;
    }
  } else if (innerTop) {
    const topMin =
      topPositions.length > 1
        ? topPositions[topPositions.length - 2].top +
          topPositions[topPositions.length - 2].h +
          PALETTE_SIBLING_GAP
        : topReservedPx;
    const topMax = viewportHeight - bottomReservedPx - innerTop.min;
    if (innerTop.top > topMax) innerTop.top = topMax;
    if (innerTop.top < topMin) innerTop.top = topMin;
  } else if (innerBottom) {
    const bottomMax =
      (bottomPositions.length > 1
        ? bottomPositions[bottomPositions.length - 2].top - PALETTE_SIBLING_GAP
        : viewportHeight - bottomReservedPx) - innerBottom.min;
    const bottomMin = topReservedPx;
    if (innerBottom.top > bottomMax) innerBottom.top = bottomMax;
    if (innerBottom.top < bottomMin) innerBottom.top = bottomMin;
  }

  // Emit layout entries. maxHeight is the budget between this palette's top
  // and its inner neighbor (or the track boundary).
  for (let i = 0; i < topPositions.length; i++) {
    const p = topPositions[i];
    const nextBarrier =
      i < topPositions.length - 1
        ? topPositions[i + 1].top
        : innerBottom
          ? innerBottom.top - PALETTE_SIBLING_GAP
          : viewportHeight - bottomReservedPx;
    const maxHeight = Math.max(p.min, nextBarrier - p.top);
    byId.set(p.id, { top: p.top, maxHeight });
  }
  for (let i = 0; i < bottomPositions.length; i++) {
    const p = bottomPositions[i];
    const outerBarrier =
      i < bottomPositions.length - 1
        ? bottomPositions[i + 1].top +
          bottomPositions[i + 1].h +
          PALETTE_SIBLING_GAP
        : viewportHeight - bottomReservedPx;
    const maxHeight = Math.max(p.min, outerBarrier - p.top);
    byId.set(p.id, { top: p.top, maxHeight });
  }

  return { byId };
}

export function PaletteTrack({
  children,
  topReservedPx = EDGE_PALETTE_MIN_TOP,
  bottomReservedPx,
}: {
  children: ReactNode;
  topReservedPx?: number;
  bottomReservedPx: number;
}) {
  const slotsRef = useRef<Map<string, SlotState>>(new Map());
  const listenersRef = useRef<Set<() => void>>(new Set());
  const vh = useViewportHeight();
  const vhRef = useRef(vh);
  vhRef.current = vh;
  const topReservedRef = useRef(topReservedPx);
  topReservedRef.current = topReservedPx;
  const bottomReservedRef = useRef(bottomReservedPx);
  bottomReservedRef.current = bottomReservedPx;

  const layoutCacheRef = useRef<LayoutResult>({ byId: new Map() });
  const fallbackLayoutCacheRef = useRef<Map<string, PaletteSlotLayout>>(
    new Map(),
  );

  const recompute = useCallback(() => {
    layoutCacheRef.current = computeLayout({
      viewportHeight: vhRef.current,
      topReservedPx: topReservedRef.current,
      bottomReservedPx: bottomReservedRef.current,
      slots: Array.from(slotsRef.current.values()),
    });
    for (const id of fallbackLayoutCacheRef.current.keys()) {
      fallbackLayoutCacheRef.current.set(id, {
        top: topReservedRef.current,
        maxHeight: EDGE_PALETTE_MIN_SCROLLABLE_HEIGHT,
      });
    }
    for (const l of listenersRef.current) l();
  }, []);

  // Recompute whenever viewport/reserved changes.
  useEffect(() => {
    recompute();
  }, [recompute]);

  const ctx = useMemo<TrackContextValue>(() => {
    return {
      get bottomReservedPx() {
        return bottomReservedRef.current;
      },
      get topReservedPx() {
        return topReservedRef.current;
      },
      register(reg) {
        const defaultOffset = reg.defaultOffsetPx ?? 0;
        const persistedOffset = loadDesiredOffset(
          reg.storageKey,
          defaultOffset,
        );
        slotsRef.current.set(reg.id, {
          id: reg.id,
          order: reg.order,
          anchor: reg.anchor,
          storageKey: reg.storageKey,
          defaultOffsetPx: defaultOffset,
          minScrollablePx:
            reg.minScrollablePx ?? EDGE_PALETTE_MIN_SCROLLABLE_HEIGHT,
          measuredHeight: 0,
          desiredOffset: persistedOffset,
        });
        recompute();
      },
      unregister(id) {
        if (slotsRef.current.delete(id)) recompute();
      },
      reportHeight(id, heightPx) {
        const slot = slotsRef.current.get(id);
        if (!slot) return;
        if (Math.abs(slot.measuredHeight - heightPx) < 0.5) return;
        slot.measuredHeight = heightPx;
        slot.desiredOffset = clampDesiredOffset(slot, slot.desiredOffset);
        recompute();
      },
      setDesiredOffset(id, offsetPx, persist) {
        const slot = slotsRef.current.get(id);
        if (!slot) return;
        const next = clampDesiredOffset(slot, offsetPx);
        if (slot.desiredOffset !== next) {
          slot.desiredOffset = next;
          recompute();
        }
        if (persist) saveDesiredOffset(slot.storageKey, next);
      },
      getLayout(id) {
        const layout = layoutCacheRef.current.byId.get(id);
        if (layout) return layout;
        let fallback = fallbackLayoutCacheRef.current.get(id);
        if (!fallback) {
          fallback = {
            top: topReservedRef.current,
            maxHeight: EDGE_PALETTE_MIN_SCROLLABLE_HEIGHT,
          };
          fallbackLayoutCacheRef.current.set(id, fallback);
        }
        return fallback;
      },
      subscribe(listener) {
        listenersRef.current.add(listener);
        return () => {
          listenersRef.current.delete(listener);
        };
      },
    };
  }, [recompute]);

  return (
    <PaletteTrackContext.Provider value={ctx}>
      {children}
    </PaletteTrackContext.Provider>
  );
}

function useTrackContext(): TrackContextValue {
  const ctx = useContext(PaletteTrackContext);
  if (!ctx) {
    throw new Error(
      "usePaletteTrackSlot must be used within a <PaletteTrack> ancestor",
    );
  }
  return ctx;
}

interface PaletteSlotHandle extends PaletteSlotLayout {
  measureRef: (el: HTMLElement | null) => void;
  onGripPointerDown: (e: React.PointerEvent) => void;
}

const DRAG_THRESHOLD = 4;

/**
 * Register a palette with the enclosing `PaletteTrack`, report its rendered
 * height via a ResizeObserver-backed ref, and receive coordinated `top` /
 * `maxHeight`. Returns a grip pointer handler that adjusts the user's desired
 * offset along the anchor axis.
 */
export function usePaletteTrackSlot(
  reg: PaletteSlotRegistration,
): PaletteSlotHandle {
  const ctx = useTrackContext();
  const regRef = useRef(reg);
  regRef.current = reg;

  // Register / unregister on mount. We intentionally key on `id` only — if
  // order or anchor change for a slot, the caller should remount (in practice
  // they're compile-time constants).
  useEffect(() => {
    ctx.register(regRef.current);
    const id = regRef.current.id;
    return () => ctx.unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  // Subscribe to layout updates.
  const layout = useSyncExternalStore(
    ctx.subscribe,
    () => ctx.getLayout(reg.id),
    () => ctx.getLayout(reg.id),
  );

  // ResizeObserver ref. useLayoutEffect-style: measure synchronously on
  // attach so the first frame uses the true height.
  const elRef = useRef<HTMLElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  const measureRef = useCallback(
    (el: HTMLElement | null) => {
      if (roRef.current) {
        roRef.current.disconnect();
        roRef.current = null;
      }
      elRef.current = el;
      if (!el) return;
      // Use the content box's natural scroll height here, not the clipped
      // client rect, so bottom-docked palettes can shrink below their
      // current visible height and later spring back to full content height.
      ctx.reportHeight(reg.id, el.scrollHeight);
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const h =
            (entry.target as HTMLElement).scrollHeight ||
            entry.contentRect.height;
          ctx.reportHeight(reg.id, h);
        }
      });
      ro.observe(el);
      roRef.current = ro;
    },
    [ctx, reg.id],
  );

  useLayoutEffect(() => {
    return () => {
      if (roRef.current) {
        roRef.current.disconnect();
        roRef.current = null;
      }
    };
  }, []);

  const onGripPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startY = e.clientY;
      const startLayout = ctx.getLayout(regRef.current.id);
      const el = elRef.current;
      const h = el ? el.scrollHeight : 0;
      const anchor = regRef.current.anchor;
      let dragging = false;

      // Convert a new proposed top into a desired offset from the anchor.
      const topToOffset = (proposedTop: number): number => {
        if (anchor === "top") {
          return Math.max(0, proposedTop - ctx.topReservedPx);
        }
        // bottom-anchored: offset is the gap between the bottom of the slot
        // and the bottom reserved edge.
        const bottomBaseline = window.innerHeight - ctx.bottomReservedPx;
        return bottomBaseline - (proposedTop + h);
      };

      const onMove = (ev: PointerEvent) => {
        const dy = ev.clientY - startY;
        if (!dragging && Math.abs(dy) < DRAG_THRESHOLD) return;
        dragging = true;
        const nextOffset = topToOffset(startLayout.top + dy);
        ctx.setDesiredOffset(regRef.current.id, nextOffset, false);
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (dragging) {
          const currentLayout = ctx.getLayout(regRef.current.id);
          const finalOffset = topToOffset(currentLayout.top);
          ctx.setDesiredOffset(regRef.current.id, finalOffset, true);
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [ctx],
  );

  return {
    top: layout.top,
    maxHeight: layout.maxHeight,
    measureRef,
    onGripPointerDown,
  };
}
